import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import type { RouterStateSnapshot } from '@angular/router';
import { LoginService } from '../../auth/login.service';

/**
 * Pure 2FA check — safe to call from AuthGuard's ensureSession map
 * (outside inject() context). TwoFactorGuard is the CanActivateFn wrapper.
 */
export function evaluateTwoFactorAccess(
  loginService: LoginService,
  router: Router,
  state: RouterStateSnapshot
): boolean {
  if (!loginService.isAuthenticated()) {
    router.navigate(['/login']);
    return false;
  }

  const user = loginService.getUser();
  if (!user) {
    router.navigate(['/login']);
    return false;
  }

  const path = state.url.split('?')[0].split('#')[0];
  if (path === '/profile' || path.startsWith('/profile/') || path.startsWith('/onboarding')) {
    return true;
  }

  if (!user.totpEnabled) {
    // Users that finished onboarding without 2FA are exceptional (legacy
    // data) — bounce them to /profile so the existing enable-2FA dialog
    // can finish the job. Users that didn't finish onboarding yet get
    // routed back to the wizard so they can complete the mandatory 2FA
    // step in context.
    if (!user.isOnboarded) {
      router.navigate(['/onboarding']);
    } else {
      router.navigate(['/profile'], {
        queryParams: { require2FA: 'true' },
      });
    }
    return false;
  }

  return true;
}

/**
 * Guard that enforces 2FA requirement.
 *
 * Routes that allow access even without 2FA:
 *   - `/profile`: legacy entry point for enabling 2FA via dialog (kept as a
 *     safety net for users who somehow reached profile with 2FA disabled —
 *     e.g. legacy data, admin reset).
 *   - `/onboarding`: the onboarding wizard now owns the mandatory 2FA
 *     setup as its final step. Without this whitelist, a freshly invited
 *     Team Member (who lands here with `totpEnabled=false`) would be
 *     bounced to `/profile?require2FA=true` and never see the wizard.
 */
export const TwoFactorGuard: CanActivateFn = (_route, state) => {
  return evaluateTwoFactorAccess(inject(LoginService), inject(Router), state);
};
